import { h, Component } from 'preact';
import isShallowEqual from 'shallow-equals';
import Store from 'store/store';

import 'store/bolt/showtimes';

import Link from 'components/bolt/link/link';
import Theater from 'components/bolt/showtimes/theater';
import LocationSearch from 'components/bolt/showtimes/locationSearch';
import FilterSelection from 'components/bolt/showtimes/filterSelection';
import IconFilter from 'components/bolt/icons/filter';
import IconList from 'components/bolt/icons/list';
import IconMap from 'components/bolt/icons/map';
import Walk from 'components/bolt/icons/walk';
import Car from 'components/bolt/icons/car';
import Rail from 'components/bolt/icons/rail';
import Button from 'components/bolt/button/button';
import ProviderInterstitial from 'components/bolt/showtimes/providerInterstitial';
import ShowtimesMessage from 'components/bolt/showtimes/showtimesMessage';
import NotificationBanner from 'components/bolt/sidePanel/notificationBanner';
import LoadIcon from 'components/bolt/sidePanel/loadIcon';
import Footer from 'components/bolt/footer/footer';
import MovieDetails from 'components/bolt/movieDetails/movieDetails';
import MovieDetailsClickableArea from 'components/bolt/movieDetails/movieDetailsClickableArea';
import DualMenu from 'components/bolt/dualMenu/dualMenu';
import { createMarker } from 'components/bolt/showtimes/mapMarker';
import { createMapTilerMarker } from 'components/bolt/showtimes/mapTilerMarker';
import mapStyles from 'components/bolt/showtimes/mapStyles.json';

import Geo from 'models/Geo';

import { accessNested, joinClasses, toQueryString, parseDate } from 'utils/utils';
import { lightness } from 'utils/colors';
import parseQueryString from 'utils/parseQueryString';
import formatDateTime from 'utils/formatDateTime';
import { smoothScrollTo } from 'utils/smoothScroll';
import { getValueForClientSize, isDualViewEnabled } from 'utils/bolt';
import { geoFromIP } from 'utils/bolt/geo';
import { getBoundingRegions, expandFilterSelection } from 'utils/bolt/showtimes';
import { getIsSinglePageIframed, getIsShowtimesOnlyIframed } from 'utils/bolt/client';
import * as gmapsUtils from 'utils/bolt/gmaps';
import * as mapTilerUtils from 'utils/bolt/mapTiler';

import s from 'components/bolt/showtimes/showtimes.sss';

const theaterModeToSource = {
	list: 'listings_view',
	expanded: 'cinema_view',
	collapsed: 'map_view'
};

export default class Showtimes extends Component {
	constructor(props) {
		super(props);

		this.showFilterSelection = this.showFilterSelection.bind(this);
		this.filtersChange = this.filtersChange.bind(this);
		this.setListMode = this.setListMode.bind(this);
		this.setMapMode = this.setMapMode.bind(this);
		this.setMapModeButton = this.setMapModeButton.bind(this);
		this.expandCurrentMapTheater = this.expandTheater.bind(this, null);
		this.selectNearby = this.selectNearby.bind(this);
		this.selectNearbyFromChain = this.selectNearbyFromChain.bind(this);
		this.hideShowtimes = this.hideShowtimes.bind(this);
		this.showMore = this.showMore.bind(this);
		this.updateMapMarkers = this.updateMapMarkers.bind(this);
		this.triggerShowtimesBtn = this.triggerShowtimesBtn.bind(this);
		this.trigger = this.trigger.bind(this);
		this.onMouseDown = this.onMouseDown.bind(this);
		this.onCHTouchStart = this.onCHTouchStart.bind(this);
		this.onCHTouchMove = this.onCHTouchMove.bind(this);
		this.scrollerClick = this.scrollerClick.bind(this);
		this.disableMobileOverlay = this.toggleMobileOverlayActive.bind(this, false);
		this.enableMobileOverlay = this.toggleMobileOverlayActive.bind(this, true);
		this.toggleMobileOverlayActive = this.toggleMobileOverlayActive.bind(this);
		this.handleFilterOverlayClick = this.handleFilterOverlayClick.bind(this);
		this.safariMobileBlur = this.safariMobileBlur.bind(this);
		this.safariMobileFocus = this.safariMobileFocus.bind(this);
		this.handleScroll = this.handleScroll.bind(this);
		this.handleLimitInterstetial = this.handleLimitInterstetial.bind(this);
		this.setGeo = this.setGeo.bind(this);
		this.applyQueryStringMovieFilter = this.applyQueryStringMovieFilter.bind(this);

		this.filtersList = ['date', 'time', 'format'];
		this.defaultChainLogo = 'default.png';

		let pages = accessNested(this.props, 'store.appData.pages', {});
		this.noHome = !Object.keys(pages).includes('boltHome') && !Object.keys(pages).includes('boltPreviewPanel');

		this.boundingRegions = getBoundingRegions();

		this.today = formatDateTime(new Date(), 'dd MMM');

		const mapTilerData = accessNested(this.props, 'store.appData.doc.mapStyling.mapTiler') || {};
		this.useMapTiler = mapTilerData.id && mapTilerData.key;

		this.userCountry = (accessNested(this.props, 'store.client.geo.countryCode') || '').toUpperCase();

		const styling = this.props.store?.appData?.styling;
		this.logoShade = styling?.logoShade;

		if (!this.logoShade) {
			const secondaryTextColor = styling?.variables?.secondaryTextColor || '#000';
			this.logoShade = lightness(secondaryTextColor) < .5 ? 'light' : 'dark';
		}

		this.state = {
			mode: 'list',
			mapTop: 0,
			mapBottom: 0,
			mapSelectedTheater: null,
			mapTheaterExpanded: false,
			animateTheaterExpansion: false,
			mobileOverlayActive: false,
			socialInterstitial: this.props.store?.limitExceeded,
			validCountry: !this.boundingRegions || this.boundingRegions.includes(this.userCountry),
			ttVisible: true
		};
	}

	componentDidMount() {
		Store.on('LIMIT_EXCEEDED', this.handleLimitInterstetial);

		if (!this.state.validCountry) {
			let userCountry = accessNested(this.props, 'store.client.geo.countryCode', '').toUpperCase();
			Store.emit('WRONG_REGION', userCountry);
		} else {
			this.updateShowtimes();
		}

		document.addEventListener('mousedown', this.onMouseDown);

		if (window.ResizeObserver) {
			this.resizeObserver = new ResizeObserver(() => this.resizeMap());
		}

		this.preventClose = window.location.hash == '#no-clicks-out';
	}

	componentWillUnmount() {
		document.removeEventListener('mousedown', this.onMouseDown);
		if (this.resizeObserver) {
			// this.resizeObserver.unobserve(this.$mapElement);
			this.resizeObserver.disconnect();
		}
		Store.off('LIMIT_EXCEEDED', this.handleLimitInterstetial);
	}

	componentWillReceiveProps(nextProps) {
		// let currentSel = this.state.mapSelectedTheater;
		// if (nextProps.store.showtimes.data !== this.props.store.showtimes.data && currentSel) {
		// 	let newTheater = (accessNested(nextProps, 'store.showtimes.data') || []).find(th => (
		// 		th.id === currentSel.id
		// 	));
		// 	if (!newTheater) {
		// 		newTheater = { ...currentSel, screenings: [] };
		// 	}
		// 	this.setState({ mapSelectedTheater: newTheater });
		// }
		if (nextProps.store.showtimes.data !== this.props.store.showtimes.data) {
			const newSelectedTheater = accessNested(nextProps, 'store.showtimes.data.0');
			this.setState({ mapSelectedTheater: newSelectedTheater });

			// SELECT_THEATER here would be a bit inconsistent with the fact that it triggers when the theater becomes in the expanded format in other cases
			// here is just when a new set of theaters is loaded
			// if (newSelectedTheater) {
			// 	Store.emit('SELECT_THEATER', newSelectedTheater);
			// }
		}

		if (accessNested(nextProps, 'store.client.isDesktopSize') && this.state.mobileOverlayActive) {
			this.toggleMobileOverlayActive(false);
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.store.showtimes.show && !this.props.store.showtimes.show) {
			this.setState({ mode: 'list' });
		}

		const geo = accessNested(this.props, 'store.client.geo') || {};
		const prevGeo = accessNested(prevProps, 'store.client.geo') || {};
		const countryCode = geo.countryCode;
		const previousCountryCode = prevGeo.countryCode;
		if (countryCode && countryCode !== previousCountryCode) {
			// const appCountry = accessNested(this.props, 'store.appData.meta.region.slug');
			// const sameCountry = (countryCode || '').toLowerCase() === (appCountry || '').toLowerCase();
			const validCountry = !this.boundingRegions || this.boundingRegions.includes(countryCode.toUpperCase());
			if (validCountry !== this.state.validCountry) {
				this.setState({ validCountry });
			}
			if (!validCountry) {
				Store.emit('WRONG_REGION', countryCode);
			}
		}
		if (geo.lat !== prevGeo.lat || geo.lon !== prevGeo.lon) {
			smoothScrollTo(this.$scroller, 0, 100);
			// On Desktop, .listView is the scrollable element
			smoothScrollTo(this.$listView, 0, 100);
		}

		if (this.$bgVideo && this.state.bgVideo !== prevState.bgVideo) {
			this.$bgVideo.src = this.state.bgVideo;
		}

		if (this.state.filterSelection && !prevState.filterSelection && this.$filterSelection) {
			// Filter selection activated !
			this.$filterSelection.focus();
			// let firstFocusableElement = this.$filterSelectionContainer.querySelector('a, input, button');
			// if (firstFocusableElement) {
			// 	firstFocusableElement.focus();
			// }
		}

		if (this.props.displayed && !prevProps.displayed) {
			// Should not focus the search input on mobile
			// this.focus();
		}

		if (this.state.mobileOverlayActive && !prevState.mobileOverlayActive) {
			this.focus();
		}

		const dualViewEnabled = isDualViewEnabled();

		if (this.$sticky && this.$filters) {
			const isDesktopSize = accessNested(this.props, 'store.client.isDesktopSize');
			const { mapTheaterExpanded } = this.state;

			let desiredMapTop = 0;
			if (isDesktopSize) {
				if (mapTheaterExpanded) {
					desiredMapTop = this.$filters.clientHeight + this.$filters.getBoundingClientRect().top;
				} else {
					desiredMapTop = this.$sticky.clientHeight + this.$collapseHeader.clientHeight;
				}
			} else {
				// const mobileMargin = this.$collapseHeader.clientHeight + parseInt(getComputedStyle(this.$content).marginTop);
				// desiredMapBottom = -mobileMargin;
				if (mapTheaterExpanded) {
					desiredMapTop = dualViewEnabled ? 64 : this.$filters.clientHeight;
				} else {
					desiredMapTop = dualViewEnabled ? 64 : this.$sticky.getBoundingClientRect().bottom - this.$container.getBoundingClientRect().top;
				}
			}

			if (this.state.mapTop !== desiredMapTop && !isNaN(desiredMapTop)) {
				this.setState({ mapTop: desiredMapTop });
			}
		}

		const lastLoading = accessNested(prevProps, 'store.showtimes.loading');
		const nowLoading = accessNested(this.props, 'store.showtimes.loading');
		const lastError = accessNested(prevProps, 'store.showtimes.error');
		const nowError = accessNested(this.props, 'store.showtimes.error');

		// Should probably not mount gmap in the componentDidUpdate without more checks
		if (this.state.mode === 'map') {
			const userLat = accessNested(this.props, 'store.client.geo.lat');
			const userLon = accessNested(this.props, 'store.client.geo.lon');

			const selectedLat = accessNested(this.state, 'mapSelectedTheater.lat');
			const selectedLon = accessNested(this.state, 'mapSelectedTheater.lon');

			const lat = selectedLon && selectedLat ? selectedLat : userLat;
			const lon = selectedLon && selectedLat ? selectedLon : userLon;
			const zoomControl = this.state.mapTheaterExpanded ? false : true;

			const options = { styles: mapStyles, center: { lat: +lat, lon: +lon }, zoomControl };
			this.mountGoogleMaps(options);

			if (lastLoading && !nowLoading) {
				this.updateMapMarkers();
			}
		}

		if (nowError && lastError !== nowError) {
			Store.emit('SHOWTIMES_ERROR');
		}

		let data = accessNested(this.props, 'store.showtimes.data');

		let prevData = accessNested(prevProps, 'store.showtimes.data');
		if (data !== prevData) {
			this.updateMapMarkers(prevData);
			if (data && !data.length) {
				Store.emit('SHOWTIMES_EMPTY', accessNested(this.props, 'store.client.geo', {}));
			}
		}
	}

	getPageId() {
		if (this.props.pageId) {
			return this.props.pageId;
		}
		let activePage = this.props.store?.activePage;
		let type = this.props.store?.appData?.pages?.[activePage]?.type;
		return type === 'showtimes' ? activePage : 'boltShowtimes';
	}

	applyQueryStringMovieFilter() {
		const queryString = parseQueryString(location.search);
		if (!queryString.m) return;
		const queryStringMovieIds = queryString.m.replace(/\s+/g, '').replace('%20', '').split(',');
		Store.emit('SET_FILTERS_SELECTED_MOVIE_ID', queryStringMovieIds);

		// with clear query string
		history.replaceState && history.replaceState(null, '', location.pathname);
	}

	onMouseDown(e) {
		const { interactivity } = e;
		if (!document.querySelector('.pageWrapper')?.contains(e.target)) {
			return;
		}
		let closestInteractiveElement = e.target.closest('input, button, a');
		if (!interactivity && !closestInteractiveElement) {
			this.hideShowtimes();
		}
	}

	onCHTouchStart(e) {
		this.xDown = (e?.touches?.[0] || e).clientX;
		this.yDown = (e?.touches?.[0] || e).clientY;
	}

	onCHTouchMove(e) {
		if (!this.xDown || !this.yDown) return;

		let xUp = e.touches[0].clientX;
		let yUp = e.touches[0].clientY;

		let xDiff = this.xDown - xUp;
		let yDiff = this.yDown - yUp;

		if (Math.sqrt(xDiff * xDiff + yDiff * yDiff) > 10.0) {
			if (Math.abs(yDiff) > Math.abs(xDiff) && yDiff <= 0) {
				// Down
				this.hideShowtimes();
			}
			this.xDown = null;
			this.yDown = null;
		}
	}

	scrollerClick(e) {
		if (e.target === this.$scroller) {
			// Clicked / tapped outside the content
			this.hideShowtimes();
		}
	}

	focus() {
		let input = this.state.mobileOverlayActive ? this.$overlayLocationSearch : this.$locationSearch;
		if (input) {
			input.focus();
		}
	}

	setListMode() {
		let state = this.state;
		if (state.mode === 'list') {
			return;
		}
		this.setState({ mode: 'list' });
	}

	handleFilterOverlayClick(e) {
		if (e.target !== this.$filterSelection.base && !this.$filterSelection.base.contains(e.target)) {
			let unchangedFilters = { ...(accessNested(this.props, 'store.showtimes.filters.selected') || {}) };
			Store.emit('REVERT_FILTERS', unchangedFilters);
			Store.emit('POP_SHOWTIMES_BTN_ACTION');
			this.setState({ filterSelection: false });
		}
	}

	handleScroll(e) {
		const distanceScrolled = e.target.scrollTop;
		const ttThreshold = window.innerWidth / 16 * 9 / 2;
		const newTTVisible = distanceScrolled <= ttThreshold;
		const { ttVisible } = this.state;

		if (newTTVisible !== ttVisible) {
			this.setState({ ttVisible: newTTVisible });
		}
	}

	toggleMobileOverlayActive(newState) {
		if (typeof newState !== 'boolean') {
			newState = !this.state.mobileOverlayActive;
		}
		if (newState === this.state.mobileOverlayActive) {
			return;
		}
		this.setState({ mobileOverlayActive: newState });

		if (newState) {
			Store.emit('SHOWTIMES_SEARCH_OVERLAY_SHOW');
			Store.emit('SET_HEADER_ACTION', {
				icon: 'back',
				action: () => {
					this.setState({ mobileOverlayActive: !this.state.mobileOverlayActive });
					Store.emit('SHOWTIMES_SEARCH_OVERLAY_HIDE');
				},
				// dark: true,
				// style: { color: '#0d0d0d' },
				tag: 'hideShowtimesSearchOverlay'
			});
		} else {
			Store.emit('REMOVE_HEADER_ACTIONS', 'hideShowtimesSearchOverlay');
		}
	}

	trackConversion(theater, screening, provider, mode, url) {
		if (!theater || !screening) {
			return;
		}

		let format = screening.format.trim() === '' ? 'absent' : screening.format;

		let conversion = [
			theater.name,
			accessNested(theater, 'chain.name'),
			screening.date,
			screening.time,
			format,
			provider
		].join('_');

		if (conversion.split('').length > 100) {
			conversion = [
				theater.name.substring(0, 50),
				accessNested(theater, 'chain.name'),
				screening.date,
				screening.time,
				format,
				provider
			].join('_');
		}

		Store.emit('TICKET_CONVERSION', {
			date: screening.date,
			time: screening.time,
			format: format,
			provider,
			conversion,
			source: theaterModeToSource[mode] || 'listings_view',
			theaterName: theater.name,
			theater: theater,
			day: formatDateTime(parseDate(screening.date), 'dddd'),
			url
		});
	}

	setMapMode() {
		let state = this.state;
		if (state.mode === 'map') {
			return;
		}
		if (!this.$collapseHeader || !this.$content || !this.$scroller) {
			return;
		}
		const isDesktopSize = accessNested(this.props, 'store.client.isDesktopSize', '');
		let minTop = this.$collapseHeader.clientHeight + parseInt(getComputedStyle(this.$content).marginTop);
		let currentTop = this.$scroller.scrollTop;
		let onReady = () => {
			this.setState({ mode: 'map' });
		};

		let needsToScroll = !isDesktopSize ? true : currentTop > 0;

		if (needsToScroll) {
			let scrollDestination = isDesktopSize ? 0 : minTop;
			smoothScrollTo(this.$scroller, scrollDestination, 100, {
				force: true,
				callback: onReady
			});
		} else {
			onReady();
		}
	}

	setMapModeButton() {
		let state = this.state;
		if (state.mode === 'map') {
			return;
		}
		this.setState({ mapTheaterExpanded: false, animateTheaterExpansion: false });
		this.setMapMode();
		this.updateMapMarkers();
	}

	showFilterSelection() {
		const isDesktopSize = accessNested(this.props, 'store.client.isDesktopSize', '');
		let unchangedFilters = { ...(accessNested(this.props, 'store.showtimes.filters.selected') || {}) };
		let backAction = () => {
			Store.emit('REVERT_FILTERS', unchangedFilters);
			this.setState({ filterSelection: false });
		};

		if (!isDesktopSize) {
			Store.emit('SET_HEADER_ACTION', {
				icon: 'back',
				action: backAction
			});
		}
		this.setState({ filterSelection: true });
		Store.emit('SET_SHOWTIMES_BTN_ACTIONS', {
			icon: 'back',
			action: backAction
		});

		if (!this.$collapseHeader || !this.$content || !this.$filterSelection) {
			return;
		}

		// 156 = margin-top of .content
		if (!isDesktopSize && this.$scroller.scrollTop < 156 && getIsSinglePageIframed()) {
			this.$scroller.scrollTop = 0;
		}
	}

	getDefaultAction() {
		let mode = this.state.mode;
		return {
			icon: 'back',
			action: () => {
				this.setState({ mapTheaterExpanded: mode !== 'list' && mode !== 'map' ? true : false, mode });
				// this.updateMapMarkers();
				this.updateMapOptions({ zoomControl: true });
			}
		};
	}

	getCurrentShowtimesBtnAction() {
		const actions = accessNested(Store.get(), 'showtimes.actions');
		return actions[actions.length - 1] || this.getDefaultAction();
	}

	getCurrentHeaderAction() {
		const actions = accessNested(Store.get(), 'header.actions');
		return actions[actions.length - 1] || this.getDefaultAction();
	}

	triggerShowtimesBtn() {
		Store.emit('POP_SHOWTIMES_BTN_ACTION');
		let actionData = this.getCurrentShowtimesBtnAction();
		if (typeof actionData.action === 'function') {
			actionData.action();
		} else if (typeof actionData.action === 'string') {
			Store.emit(actionData.action);
		}
	}

	trigger() {
		Store.emit('POP_HEADER_ACTION');
		let actionData = this.getCurrentHeaderAction();
		if (typeof actionData.action === 'function') {
			actionData.action();
		} else if (typeof actionData.action === 'string') {
			Store.emit(actionData.action);
		}
	}

	expandTheater(theater, doNotUpdateActions) {
		Store.emit('UPDATE_API_KEY');
		Store.emit('GET_NEARBY_CINEMAS_FROM_CHAIN', theater || this.state.mapSelectedTheater);
		Store.emit('IN_CINEMA_DETAIL_VIEW');

		let sourceMode = this.state.mode;

		const newTheater = theater || this.state.mapSelectedTheater;
		Store.emit('SELECT_THEATER', newTheater, sourceMode);

		if (!doNotUpdateActions) {
			this.modeWhenTheaterExpanded = sourceMode;
			const isDesktopSize = accessNested(this.props, 'store.client.isDesktopSize');
			if (!isDesktopSize) {
				Store.emit('SET_HEADER_ACTION', {
					icon: 'back',
					action: () => {
						this.exitCinemaDetailView(sourceMode);
					}
				});
			} else {
				Store.emit('SET_SHOWTIMES_BTN_ACTIONS', {
					icon: 'back',
					action: () => {
						this.exitCinemaDetailView(sourceMode);
					}
				});
			}
		}
		let newState = { mapTheaterExpanded: true, animateTheaterExpansion: sourceMode === 'map' };
		if (theater) {
			newState.mapSelectedTheater = theater;
		}

		this.setState(newState);
		this.setMapMode();
		this.updateMapMarkers();
		this.updateMapOptions({ zoomControl: false });
	}

	exitCinemaDetailView(sourceMode) {
		this.setState({ mapTheaterExpanded: sourceMode !== 'list' && sourceMode !== 'map' ? true : false, mode: sourceMode });
		this.updateMapMarkers();
		this.updateMapOptions({ zoomControl: true });
		Store.emit('EXIT_CINEMA_DETAIL_VIEW');
	}

	filtersChange() {
		Store.emit('UPDATE_API_KEY');
		this.setState({ filterSelection: false });

		// const isCinemaDetail = accessNested(this.props, 'store.showtimes.inCinemaDetailView', false);
		// if (isCinemaDetail) {
		// 	this.exitCinemaDetailView(this.modeWhenTheaterExpanded);
		// }

		let filters = accessNested(this.props, 'store.showtimes.filters.selected') || {};
		let snapshot = accessNested(this.props, 'store.showtimes.filterSnapshot') || {};

		// Remove from query string (SUP-598)
		let qs = parseQueryString();
		let changes = Object.keys(filters).map(filter => {
			if (!qs[filter] || typeof qs[filter] !== 'string') {
				return;
			}
			let value = filters[filter];
			if (!Array.isArray(value)) {
				value = [value];
			}
			let newValue = qs[filter].split(',').map(e => e.trim()).filter(e => e && value.includes(e)).join(',');
			if (newValue === qs[filter]) {
				return;
			}
			qs[filter] = newValue || null;
			return true;
		});
		if (changes.some(e => e)) {
			let newUrl = location.pathname + toQueryString(qs) + location.hash;
			history.replaceState({ activePage: Store.get().activePage }, '', newUrl);
		}

		const dateChanged = !isShallowEqual((filters.date || []).sort(), (snapshot.date || []).sort());
		const timeChanged = !isShallowEqual((filters.time || []).sort(), (snapshot.time || []).sort());
		const formatChanged = !isShallowEqual((filters.format || []).sort(), (snapshot.format || []).sort());
		const anyFilterChange = JSON.stringify(filters) !== JSON.stringify(snapshot);

		if (anyFilterChange) {
			// Store.emit('SET_MOVIE_FILTER_ID', filters.movie?.length ? filters.movie : '' );
			Store.emit('SET_FILTERS_SELECTED_MOVIE_ID', filters.movie?.length ? filters.movie : [] );
			Store.emit('SHOWTIMES_REFRESH');
			Store.emit('TRACK_FILTERS_CHANGE', filters);
		}
		if (dateChanged) {
			Store.emit('FILTER_SELECTED_DAY', filters.date[0]);
		}
		if (timeChanged) {
			Store.emit('FILTER_SELECTED_TIME', filters.time.join(','));
		}
		if (formatChanged) {
			Store.emit('FILTER_SELECTED_FORMAT', filters.format.join(','));
		}
		const isDesktopSize = accessNested(this.props, 'store.client.isDesktopSize');
		if (isDesktopSize) {
			Store.emit('POP_SHOWTIMES_BTN_ACTION');
			return;
		}
		Store.emit('POP_HEADER_ACTION');
	}

	renderDummyElements(count) {
		if (!this.state.validCountry) {
			return;
		}
		return [...Array(count)].map((e, i) => (
			<div class={s.dummy} key={i}>
				<div class={s.titleLine}>
					<div class={s.icon} />
					<div class={s.title} />
				</div>
				<div class={s.screenings}>
					<div class={s.screening} />
					<div class={s.screening} />
					<div class={s.screening} />
					<div class={s.screening} />
				</div>
			</div>
		));
	}

	filterValue(filter, value) {
		if (filter === 'date') {
			let parts = value.split('-');
			let date = new Date(+parts[0], parts[1] - 1, +parts[2]);
			const dateTimeLocale = accessNested(this.props, 'store.appData.copy.baseCopy.$DATETIME_LOCALE');
			let formattedDate = formatDateTime(date, 'dd MMM', dateTimeLocale);
			if (formattedDate === this.today) {
				const today = accessNested(this.props, 'store.appData.copy.baseCopy.$TODAY', 'today');
				return today.charAt(0).toUpperCase() + today.slice(1);
			}
			return formattedDate;
		}
		return accessNested(this.props, ['store', 'appData', 'copy', 'baseCopy', 'filters', filter, value]) || value;
	}

	mountGoogleMaps(options) {
		if (this.gmap || !this.$mapElement) {
			return;
		}
		const mapUtils = this.useMapTiler ? mapTilerUtils : gmapsUtils;
		this.gmap = mapUtils.mount(this.$mapElement, options);

		if (this.resizeObserver) {
			this.resizeObserver.observe(this.$mapElement);
		}
		this.updateMapMarkers();
	}

	async resizeMap() {
		const map = await this.gmap;
		if (map.resize) {
			map.resize();
		}
	}

	hideShowtimes() {
		const isSinglePageIframed = getIsSinglePageIframed();
		if (this.noHome || isSinglePageIframed) return;

		const isDesktopSize = accessNested(this.props, 'store.client.isDesktopSize');
		const action = isDesktopSize ? this.triggerShowtimesBtn : this.trigger;
		action();
		this.setState({ filterSelection: false, mode: 'list' });
		Store.emit('SHOWTIMES_HIDE', true, true);
	}

	showMore() {
		Store.emit('SHOWTIMES_SHOW_MORE');
	}

	async updateMapOptions(opts) {
		if (!this.gmap && typeof opts === 'object') {
			return;
		}

		let gmap = await this.gmap;
		if (gmap.setOptions) {
			gmap.setOptions(opts);
		}
	}

	async updateMapMarkers(previousTheaters) {
		if (!this.gmap) {
			return;
		}
		const sharedImageRoot = accessNested(this.props, 'store.appData.urls.roots.sharedImages');
		const expanded = this.state.mapTheaterExpanded;
		let gmap = await this.gmap;

		let data = accessNested(this.props, 'store.showtimes.data') || [];
		if (!previousTheaters && data !== previousTheaters) {
			if (this.mapMarkers) {
				this.mapMarkers.forEach(marker => marker.setMap(null));
			}

			if (expanded) {
				data = data.filter(e => this.state.mapSelectedTheater.id === e.id);
			}

			let defaultActiveShade = 'light';

			const variableWhite = accessNested(this.props, 'store.appData.styling.variables.white');
			const variableScreeningBtnTxtColor = accessNested(this.props, 'store.appData.styling.variables.screeningBtnTxtColor');

			if (variableWhite === variableScreeningBtnTxtColor) {
				defaultActiveShade = 'dark';
			}

			const activeShade = accessNested(this.props, 'store.appData.styling.variables.activeMapMarkerShade', defaultActiveShade);
			const markerCreator = this.useMapTiler ? createMapTilerMarker : createMarker;

			this.mapMarkers = await Promise.all(data.map(theater => {
				const isActive = this.state.mapSelectedTheater.id === theater.id;
				const logo = theater.chain.logo || this.defaultChainLogo;
				const activeLogoLink = `${sharedImageRoot}/logos/${activeShade}/${logo}`;
				const inactiveLogoLink = `${sharedImageRoot}/logos/dark/${logo}`;

				return markerCreator({
					position: { lat: theater.lat, lon: theater.lon },
					activeLogoLink,
					inactiveLogoLink,
					onClick: (marker) => this.updateMapSelectedTheater(theater, marker),
					isActive
				}, gmap);
			}));

			const centrSettings = {};
			const { geo } = Store.get().client;
			if (this.state.mapSelectedTheater) {
				centrSettings.lat = accessNested(this.state, 'mapSelectedTheater.lat');
				centrSettings.lng = accessNested(this.state, 'mapSelectedTheater.lon');
			} else if (geo) {
				centrSettings.lat = accessNested(geo, 'lat');
				centrSettings.lng = accessNested(geo, 'lon');
			}

			if (Object.keys(centrSettings).length) {
				gmap.setCenter(centrSettings);
			}
		}
	}

	selectNearby(selection) {
		Store.emit('SET_USER_LOCATION', selection, 'nearby');
	}
	selectNearbyFromChain(theater) {
		this.expandTheater(theater, true);
	}

	setDirections(travelMode) {
		const { mapSelectedTheater } = this.state;

		const destLat = mapSelectedTheater.lat;
		const destLng = mapSelectedTheater.lon;

		if (destLat && destLng) {
			const link = `https://www.google.com/maps/dir/?api=1&destination=${destLat},${destLng}&travelmode=${travelMode.toLowerCase()}`;
			Store.emit('MAP_REQUEST_DIRECTIONS', travelMode);
			window.open(link);
		}
	}

	updateMapSelectedTheater(theater, marker) {
		this.setState({ mapSelectedTheater: theater });
		this.mapMarkers.forEach(marker => {
			if (marker.isActive) {
				marker.makeInactive();
			}
		});
		marker.makeActive();
		this.updateMapMarkers();
	}

	async setGeo() {
		const data = await geoFromIP();
		Store.emit('GEO_FROM_IP', data);
		this.setState({ geoFromIP: data });
	}

	handleLimitInterstetial(isExceeded) {
		if (this.state.socialInterstitial === isExceeded) {
			return;
		}
		this.setState({ socialInterstitial: isExceeded });
	}

	async updateShowtimes() {
		await this.setGeo();
		this.applyQueryStringMovieFilter();
		Store.emit('FILTERS_REFRESH', { forceShowtimesRefresh: true });
	}

	renderCurrentFilters() {
		const { validCountry } = this.state;
		// For some reason that breaks the map view
		// Hide it with display none for now
		// if (!validCountry) {
		// 	return;
		// }
		const accessibilityCopy = accessNested(this.props, 'store.appData.copy.accessibilityCopy', {});
		const isRTL = accessNested(this.props, 'store.appData.styling.fontVariables.textDirection') === 'rtl';
		const loading = accessNested(this.props, 'store.showtimes.loading') || false;
		let disabled = loading;

		let filters = accessNested(this.props, 'store.showtimes.filters') || {};
		let selected = filters.selected || {};

		let buttons = this.filtersList.map(filter => {
			let sel = expandFilterSelection(selected[filter] || [], filters.available?.[filter]);
			// TODO: find better way of replicating "mergeWith" from filterSelection component
			if (filter === 'format') {
				sel = sel.concat(expandFilterSelection(selected.audioDetails || [], filters.available?.audioDetails)).sort();
			}
			let label;
			if (sel.length) {
				label = sel.map(v => this.filterValue(filter, v)).join(', ');
				// Display custom text when over a certain length ? e.g. "5 formats"
			} else {
				label = this.props.store?.appData?.copy?.baseCopy?.filters?.[filter]?._all;
			}
			if (!label) {
				return;
			}
			let filterBtnAria;

			if (filter === 'date') {
				filterBtnAria = accessibilityCopy.$DAY_FILTER_ARIA;
			}
			if (filter === 'time') {
				filterBtnAria = accessibilityCopy.$TIME_FILTER_ARIA;
			}
			if (filter === 'format') {
				filterBtnAria = accessibilityCopy.$FORMAT_FILTER_ARIA;
			}
			return (
				<Button class={joinClasses(s.filter, s[filter], isRTL && s.rtlFilter)} trigger={this.showFilterSelection} disabled={disabled} ariaLabel={filterBtnAria.replace('<option>', label)}>
					{label}
				</Button>
			);
		});
		let hidden = !validCountry || this.state.socialInterstitial || this.noFiltersAvailable;
		return (
			<div class={joinClasses(s.filters, hidden && s.hidden)} ref={e => this.$filters = e} key="currentFilters">
				<Button
					class={joinClasses(s.filter, s.toggle, isRTL && s.rtlFilter)}
					trigger={this.showFilterSelection}
					disabled={disabled}
					ariaLabel={accessibilityCopy.$OPEN_FILTER_OPTIONS_ARIA}
				>
					<IconFilter class={s.filterIcon} ariaLabel={accessibilityCopy.$OPEN_FILTER_OPTIONS_ARIA} />
				</Button>
				{buttons}
				<div class={s.spacer} />
			</div>
		);
	}

	renderModeSelector() {
		const loading = accessNested(this.props, 'store.showtimes.loading');
		const fontStyles = accessNested(this.props, 'store.appData.styling.fontVariables');
		const { mode, mapSelectedTheater, mapTheaterExpanded } = this.state;
		let copy = accessNested(this.props, 'store.appData.copy', {});

		let icons = <LoadIcon />;

		if (!loading) {
			let setListMode = this.setListMode;
			// Need to use the menu back arrow to go back
			if (mapTheaterExpanded && mapSelectedTheater) {
				setListMode = undefined;
			}
			const customMapIcon = accessNested(this.props, 'store.appData.styling.imageVariables.showtimesMapViewIcon', '');
			icons = [
				<Button class={joinClasses({ s, listIcon: true, active: (mode === 'list' && loading === false) })} trigger={setListMode} ariaLabel={copy.accessibilityCopy.$LIST_VIEW_ARIA}>
					<IconList aria-hidden />
				</Button>,
				<Button class={joinClasses({ s, mapIcon: true, active: mode === 'map', custom: customMapIcon })} trigger={this.setMapModeButton} ariaLabel={copy.accessibilityCopy.$MAP_VIEW_ARIA}>
					{customMapIcon ? <div class={s.customMapIcon} style={`-webkit-mask: url(${customMapIcon}) no-repeat center;`} /> : <IconMap aria-hidden />}
				</Button>
			];
		}

		let ctaText = copy.baseCopy.$SELECT_A_SHOWTIME;
		if (loading) {
			ctaText = copy.baseCopy.$LOADING_CINEMAS;
		} else if (!(accessNested(this.props, 'store.showtimes.data') || []).length) {
			ctaText = '';
		} else if (mode === 'map') {
			ctaText = copy.baseCopy.$SELECT_A_CINEMA;
		}

		return (
			<div class={s.modeWrapper}>
				<NotificationBanner
					isShowtimes
					region={this.props.store?.client?.geo?.countryCode}
					movies={this.props.store?.showtimes?.movies}
				/>
				<div class={s.mode}>
					<div class={joinClasses(s.cta, fontStyles.showtimesLoading)}>
						{ctaText}
					</div>
					<div class={s.icons}>
						{icons}
					</div>
				</div>
			</div>
		);
	}

	renderElements() {
		const state = this.state;
		if (!state.validCountry) {
			return;
		}
		const data = accessNested(this.props, 'store.showtimes.data', []);
		const showtimesAppData = accessNested(this.props, `store.appData.pages.${this.getPageId()}.data`, {});
		// const error = accessNested(this.props, 'store.showtimes.error');
		const hasMore = accessNested(this.props, 'store.showtimes.more', false);
		const loadingMore = accessNested(this.props, 'store.showtimes.loadingMore') || false;
		const copy = accessNested(this.props, 'store.appData.copy');
		const clientGeo = accessNested(this.props, 'store.client.geo', {});
		const sharedImageRoot = accessNested(this.props, 'store.appData.urls.roots.sharedImages', '');
		const fontStyles = accessNested(this.props, 'store.appData.styling.fontVariables');

		const elements = data.map((theater, i) => (
			<Theater
				theater={theater}
				mode="list"
				clientGeo={clientGeo}
				geoFromIP={state.geoFromIP || {}}
				onExpandClick={() => this.expandTheater(theater)}
				sharedImageRoot={sharedImageRoot}
				logoShade={this.logoShade}
				copy={copy}
				showtimesAppData={showtimesAppData}
				key={'theater-' + theater.id + i}
				showFilterSelection={this.showFilterSelection}
				trackConversion={this.trackConversion}
				divideScreeningsByAttributes
			/>
		));
		if (hasMore) {
			elements.push(
				<div class={s.more} key="more">
					<Button class={joinClasses(s.moreButton, loadingMore && s.loadingMore, fontStyles.showtimesShowMore)} trigger={this.showMore} ariaLabel={copy.accessibilityCopy.$MORE_CINEMAS_BTN_ARIA}>
						{loadingMore ? <LoadIcon class={s.loadingMoreIcon} /> : copy.baseCopy.$SHOW_MORE_CINEMAS}
					</Button>
				</div>
			);
		}
		return elements;
	}

	renderNearby(storeData, cta, buttonText, buttonsAria, buttonAction, count) {
		const { data, loading, error } = storeData;
		const fontStyles = accessNested(this.props, 'store.appData.styling.fontVariables');
		const clientGeo = accessNested(this.props, 'store.client.geo', {});
		const currentGeo = clientGeo && new Geo({ lat: clientGeo.lat, lon: clientGeo.lon });
		const distanceUnit = accessNested(this.props, `store.appData.pages.${this.getPageId()}.data.theaterDistanceUnit`, 'km');

		if (!this.state.validCountry || error || (!loading && !data.length)) {
			return;
		}

		let content;
		if (loading) {
			content = Array(6).fill(<div class={s.nearbyTheaterDummy} />);
		} else {
			content = data.slice(0, count).map(element => {
				const distance = currentGeo.distanceFrom(element, distanceUnit, 1);

				return (
					<Button
						class={s.nearbyTheater}
						trigger={() => buttonAction(element)}
						ariaLabel={buttonsAria.replace('<cinema>', element.name).replace('<city>', element.city)}
						key={element.id}
					>
						<span class={s.nearbyName}>{element[buttonText]}</span>
						<span class={s.nearbyDistance}>{`${distance}${distanceUnit}`}</span>
					</Button>
				);
			});
		}

		return (
			<div class={s.nearby}>
				<div class={joinClasses(s.cta, fontStyles.moreShowtimes)}>{cta}</div>
				<div class={s.nearbyList}>{content}</div>
			</div>
		);
	}

	renderNearbyFromChain() {
		let storeData = accessNested(this.props, 'store.showtimes.nearby.chain') || {};
		let cta = accessNested(this.props, 'store.appData.copy.baseCopy.$MORE_SHOWTIMES_CINEMAS') || 'More cinemas from the same chain';
		let aria = accessNested(this.props, 'store.appData.copy.accessibilityCopy.$CINEMAS_SAME_CHAIN_BTN_ARIA') || '';
		return this.renderNearby(storeData, cta, 'name', aria, this.selectNearbyFromChain, 10);
	}
	renderNearbyScreeningsLocations() {
		let storeData = accessNested(this.props, 'store.showtimes.nearby.locations') || {};
		let cta = accessNested(this.props, 'store.appData.copy.baseCopy.$MORE_SHOWTIMES_LOCATIONS') || 'More showtimes found in these locations';
		let aria = accessNested(this.props, 'store.appData.copy.accessibilityCopy.$OTHER_LOCATIONS_BTN_ARIA') || '';
		return this.renderNearby(storeData, cta, 'city', aria, this.selectNearby, undefined);
	}

	renderMap() {
		let state = this.state;
		let directions;
		let theaterContainer = this.renderVisualState(s.mapMessage, true);
		const showtimesAppData = accessNested(this.props, `store.appData.pages.${this.getPageId()}.data`, {});
		const copy = accessNested(this.props, 'store.appData.copy');
		const clientGeo = accessNested(this.props, 'store.client.geo', {});
		const loading = accessNested(this.props, 'store.showtimes.loading') || false;
		const isDesktopSize = accessNested(this.props, 'store.client.isDesktopSize');
		const sharedImageRoot = accessNested(this.props, 'store.appData.urls.roots.sharedImages') || '';

		if (state.mapSelectedTheater) {
			theaterContainer = (
				<div class={s.theaterContainer} key="theaterContainer">
					<Theater
						theater={state.mapSelectedTheater}
						mode={state.mapTheaterExpanded ? 'expanded' : 'collapsed'}
						onExpandClick={() => this.expandCurrentMapTheater()}
						animateExpansion={state.animateTheaterExpansion}
						clientGeo={clientGeo}
						geoFromIP={state.geoFromIP}
						isDesktopSize={isDesktopSize}
						sharedImageRoot={sharedImageRoot}
						logoShade={this.logoShade}
						copy={copy}
						showtimesAppData={showtimesAppData}
						showFilterSelection={this.showFilterSelection}
						trackConversion={this.trackConversion}
						loading={loading}
					/>
				</div>
			);
			directions = (
				<div class={s.directions} key="directions">
					<Button
						class={joinClasses(s.directionsType, s.walking)}
						trigger={() => this.setDirections('WALKING')}
						ariaLabel={copy.accessibilityCopy.$WALKING_BTN_ARIA}
					>
						<Walk ariaLabel={copy.accessibilityCopy.$WALKING_BTN_ARIA}/>
					</Button>
					<Button
						class={joinClasses(s.directionsType, s.driving)}
						trigger={() => this.setDirections('DRIVING')}
						ariaLabel={copy.accessibilityCopy.$CAR_BTN_ARIA}
					>
						<Car ariaLabel={copy.accessibilityCopy.$CAR_BTN_ARIA}/>
					</Button>
					<Button
						class={joinClasses(s.directionsType, s.publicTransports)}
						trigger={() => this.setDirections('TRANSIT')}
						ariaLabel={copy.accessibilityCopy.$TRANSPORT_BTN_ARIA}
					>
						<Rail ariaLabel={copy.accessibilityCopy.$TRANSPORT_BTN_ARIA}/>
					</Button>
				</div>
			);
		}

		if (loading) {
			// theaterContainer = this.renderDummyElements(1);
			directions = null;
		}

		let triggerAction = state.mapSelectedTheater && isDesktopSize ? this.triggerShowtimesBtn : this.trigger;
		const mapDisabled = state.mapTheaterExpanded;
		const showtimesFooterBackgroundColorFallback = accessNested(this.props, 'store.appData.styling.variables.tertiaryColor');
		const strechedMap = !state.mapTheaterExpanded && state.mode !== 'list';

		return (
			<div class={s.mapView} style={{ top: state.mapTop + 'px', bottom: state.mapBottom + 'px' }}>
				<div style={{ display: strechedMap ? 'flex' : '' }} class={s.theaterScroller} ref={ref => this.$theaterScroller = ref}>
					<Button class={joinClasses(s.map, mapDisabled && s.mapDisabled, strechedMap && s.stretched)} trigger={triggerAction} key="map" ariaLabel={copy.accessibilityCopy.$MAP_ARIA}>
						<div class={s.mapElement} ref={e => this.$mapElement = e} key="mapElement" />
						{directions}
						<div class={joinClasses(s.mapOverlay, mapDisabled && s.visible)} />
					</Button>
					{theaterContainer}
					<div class={s.suggestions}>
						{this.renderNearbyFromChain(state.mapSelectedTheater)}
					</div>
					{mapDisabled && (
						<Footer
							data={this.props.store.appData.footer}
							parent="showtimes"
							scrollElem={this.$theaterScroller}
							fallBackColor={showtimesFooterBackgroundColorFallback}
							key="footer"
							store={this.props.store}
						/>
					)}
				</div>
			</div>
		);
	}

	renderProviderInterstitial() {
		const { mode, mapTheaterExpanded } = this.state;
		const shouldShowProviderInterstitial = accessNested(this.props, 'store.showtimes.providerInterstitialOpen', false);

		if (!shouldShowProviderInterstitial) {
			return;
		}

		const selectedTheater = accessNested(this.props, 'store.showtimes.selectedTheater', {});
		const selectedScreening = accessNested(this.props, 'store.showtimes.selectedScreening', {});
		const sharedImageRoot = accessNested(this.props, 'store.appData.urls.roots.sharedImages');
		const isDesktopSize = accessNested(this.props, 'store.client.isDesktopSize');
		const copy = accessNested(this.props, 'store.appData.copy');

		let finalMode = mode;
		if (finalMode === 'map') {
			finalMode = mapTheaterExpanded ? 'expanded' : 'collapsed';
		}

		return (
			<ProviderInterstitial
				selectedTheater={selectedTheater}
				selectedScreening={selectedScreening}
				sharedImageRoot={sharedImageRoot}
				logoShade={this.logoShade}
				isDesktopSize={isDesktopSize}
				copy={copy}
				mode={finalMode}
				trackConversion={this.trackConversion}
				key="provider-interstitial"
			/>
		);
	}

	renderVisualState(extraClass, isMap) {
		const {
			data,
			error,
			loading,
			activeVideo = {},
			videosLoading,
			videos = []
		} = this.props.store?.showtimes || {};
		const copy = this.props.store?.appData?.copy || {};
		const deviceSize = this.props.store?.client?.deviceSize;
		const {
			noShowtimes,
			preferNoShowtimesMessage
		} = this.props.store?.appData?.pages?.[this.getPageId()]?.data || {};
		const { validCountry } = this.state;

		const region = (this.props?.store?.client?.geo?.countryCode || '').toLowerCase();
		const localisations = noShowtimes?.localisations;
		let regionLocalisation;
		if (localisations) {
			regionLocalisation = Object.values(localisations).find(l => {
				if (!l.regions) return false;
				const regions = l.regions.toLowerCase().split(',').map(r => r.trim()).filter(r => r);
				return regions.includes(region);
			});
		}

		let useNoShowtimes = preferNoShowtimesMessage && regionLocalisation;

		if (!validCountry && !loading && !useNoShowtimes) {
			const userCity = this.props.store.client.geo.city;
			let regionData = this.props.store.appData.meta.region;
			let appRegion = regionData.countryName;
			let uppercaseSlug = (regionData.slug || '').toUpperCase();
			if (this.boundingRegions.length > 1 || this.boundingRegions?.[0] !== uppercaseSlug) {
				let sortedRegions = this.boundingRegions.slice().sort((a, b) => a === uppercaseSlug ? -1 : b === uppercaseSlug ? 1 : 0);
				appRegion = sortedRegions.join(', ');
			}
			// return showtimes message with suggestion to search new location.
			let cta = copy.baseCopy.$ERROR_SHOWTIMES_LOCATION_CTA || 'Change your search location for new results';
			let message = copy.baseCopy.$ERROR_SHOWTIMES_LOCATION || 'It looks like you’re in %USER_CITY%, and this page is for users in %APP_REGION%';

			return (
				<ShowtimesMessage
					class={extraClass}
					deviceSize={deviceSize}
					isMap={isMap}
					icon="calendar-empty"
					cta={cta}
					message={message.replace('%USER_CITY%', userCity).replace('%APP_REGION%', appRegion)}
					key="message-bounding-region"
				/>
			);
		}


		if (this.state.socialInterstitial) {
			const userCity = this.props.store.client.geo.city;

			let message = copy.baseCopy.$SOCIAL_INTERSTITIAL_SHOWTIMES_INSTRUCTION || 'It looks like you\'re located in the %USER_CITY%<br>Click the button below to display showtimes around you';

			message = message.replace('%USER_CITY%', userCity);

			return (
				<ShowtimesMessage
					class={extraClass}
					deviceSize={deviceSize}
					isMap={isMap}
					message={message}
					buttonCopy={copy.baseCopy.$SOCIAL_INTERSTITIAL_SHOWTIMES_CTA || 'Display Showtimes'}
					buttonTrigger={() => {
						Store.emit('UPDATE_API_KEY');
						Store.emit('LIMIT_EXCEEDED', false);
						Store.emit('SHOWTIMES_REFRESH');
					}}
					key="message-error"
					icon="location"
				/>
			);
		}

		if (error) {
			return (
				<ShowtimesMessage
					class={extraClass}
					deviceSize={deviceSize}
					isMap={isMap}
					message={copy.baseCopy.$ERROR_SHOWTIMES}
					cta={copy.baseCopy.$ERROR_SHOWTIMES_CTA}
					buttonCopy={copy.baseCopy.$REFRESH || 'Refresh'}
					buttonTrigger={() => Store.emit('SHOWTIMES_REFRESH')}
					key="message-error"
					icon="calendar-empty"
				/>
			);
		}


		if (!loading && ((data && !data.length) || useNoShowtimes)) {
			let noShowtimesMessage = copy.baseCopy.$NO_SHOWTIMES;
			let noShowtimesCta = copy.baseCopy.$NO_SHOWTIMES_CTA;
			let regionButtonsOverride = {};

			if (regionLocalisation?.title) {
				noShowtimesMessage = copy.baseCopy[regionLocalisation.title] || regionLocalisation.title;
			}
			if (regionLocalisation?.text) {
				noShowtimesCta = copy.baseCopy[regionLocalisation.text] || regionLocalisation.text;
			}

			if (regionLocalisation?.buttons?.filtersButton !== undefined) {
				regionButtonsOverride.filtersButton = regionLocalisation.buttons.filtersButton;
			}

			if (regionLocalisation?.buttons?.extraButton) {
				regionButtonsOverride.extraButton = regionLocalisation.buttons.extraButton;
			}

			return (
				<ShowtimesMessage
					class={extraClass}
					deviceSize={deviceSize}
					isMap={isMap}
					message={noShowtimesMessage}
					cta={noShowtimesCta}
					copy={copy}
					buttonCopy={copy.baseCopy.$FILTERS || 'Filters'}
					buttonTrigger={this.showFilterSelection}
					noShowtimes={!isMap ? noShowtimes : false}
					regionButtonsOverride={regionButtonsOverride}
					noFiltersAvailable={this.noFiltersAvailable}
					activeVideo={activeVideo}
					videosLoading={videosLoading}
					showtimesVideos={videos}
					icon="coming-soon"
					key="message-empty"
				/>
			);
		}
	}

	getTTPosition() {
		let tt = accessNested(this.props, `store.appData.pages.${this.getPageId()}.data.tt`, {});

		if (!tt.titleTreatmentPosition || (!tt.titleTreatmentPosition.desktop && !tt.titleTreatmentPosition.tablet && !tt.titleTreatmentPosition.mobile)) {
			tt = accessNested(this.props, 'store.appData.pages.boltHome.data.tt', {});
		}
		return getValueForClientSize(tt.titleTreatmentPosition);
	}

	safariMobileBlur() {
		if (accessNested(this.props.store, 'client.isIOS')) {
			Store.emit('SAFARI_MOBILE_LOCATION_SEARCH_BLUR');
		}
	}

	safariMobileFocus() {
		if (accessNested(this.props.store, 'client.isIOS')) {
			Store.emit('SAFARI_MOBILE_LOCATION_SEARCH_FOCUS');
		}
	}

	renderLocationSearch(mobileOverlay) {
		const props = this.props;
		const client = props.store.client || {};
		const showtimes = props.store.showtimes || {};
		const showtimesAppData = this.props.store.appData?.pages?.[this.getPageId()]?.data || {};
		let extraProps = {};
		if (mobileOverlay) {
			extraProps = {
				mobileOverlayActive: true,
				onBlur: () => Store.emit('SAFARI_MOBILE_LOCATION_SEARCH_BLUR'),
			};
		}

		const onFocus = () => {
			Store.emit('UPDATE_API_KEY');

			if (!client.isDesktopSize) {
				if (mobileOverlay) {
					Store.emit('SAFARI_MOBILE_LOCATION_SEARCH_FOCUS');
				} else {
					this.enableMobileOverlay();
				}
			}
		};

		const onSelect = () => {
			Store.emit('LIMIT_EXCEEDED', false);
			this.disableMobileOverlay();
		};

		return (
			<LocationSearch
				class={s.location}
				ref={e => this[mobileOverlay ? '$overlayLocationSearch' : '$locationSearch'] = e}
				disabled={showtimes.loading}
				geo={client.geo}
				showtimesAppData={showtimesAppData}
				nearbyLocations={showtimes.nearby?.locations?.data}
				isMobile={!client.isDesktopSize}
				onFocus={onFocus}
				onSelect={onSelect}
				onSearch={this.disableMobileOverlay}
				movieId={showtimes?.movieFilterId}
				{...extraProps}
				key="location-search"
			/>
		);
	}

	renderAttribution() {
		let appData = this.props.store?.appData;
		let pageData = appData?.pages?.[this.getPageId()]?.data;
		let deeplinkProviders = pageData?.providers?.deeplink?.split(',')?.map(e => e.trim()) || [];
		const countryCode = (this.props?.store?.client?.geo?.countryCode || '').toLowerCase();
		if (deeplinkProviders.includes('cinergy') && countryCode === 'ch') {
			let sharedImageRoot = appData?.urls?.roots?.sharedImages;
			let logoLink = `${sharedImageRoot}/data_providers/${this.logoShade}/cineman_${this.logoShade}.png`;
			let messaging = appData?.copy?.baseCopy?.$SHOWTIMES_BY_CINEMAN;
			return (
				<div class={joinClasses(s.attribution, s.cineman)}>
					<span class={s.showtimesBy}>{messaging}</span>
					<Link href="https://www.cineman.ch/" target="_blank" key="cinergy"><img class={s.logo} src={logoLink} alt="Cineman Logo" height="24" /></Link>
				</div>
			);
		}
	}

	render(props) {
		const isShowtimesOnlyIframed = getIsShowtimesOnlyIframed();
		const allShowtimesMovies = accessNested(this.props, 'store.showtimes.allMovies', {});

		// Not a fan of this but avoids recalculating or passing things down as arguments
		let availableFilters = accessNested(props, 'store.showtimes.filters.available') || {};
		this.noFiltersAvailable = !Object.values(availableFilters).some(f => Array.isArray(f) && f.length);

		let state = this.state;
		const store = props.store;
		let { showtimes, client, appData } = store;
		const { copy } = appData;

		let showtimesAppData = accessNested(appData, `pages.${this.getPageId()}.data`) || {};
		const scrollElem = client.isDesktopSize ? this.$listView : this.$scroller;
		const cookies = accessNested(props, 'store.cookies') || {};
		const sonyHeaderEnabled = !isShowtimesOnlyIframed && accessNested(appData, 'options.sonyFeatures.sonyHeader', false);

		const evidonMode = accessNested(store, 'evidon.bannerOpen');
		const cookieMode = evidonMode || (accessNested(cookies, 'pushDown', false) && accessNested(cookies, 'cookieBannerOpen', false));
		let style = {};
		let cookieHeight = 0;
		let containerStyle = {};
		if (cookieMode || evidonMode) {
			cookieHeight = evidonMode ? accessNested(store, 'evidon.height') : accessNested(cookies, 'height');
			style.top = cookieHeight + 'px';
			style.height = 'calc(var(--windowHeight) - ' + cookieHeight + 'px)';

			if (client.isDesktopSize) {
				containerStyle.left = ((window.innerHeight - cookieHeight - (sonyHeaderEnabled ? 35 : 0)) / 900 * 609) + 'px';
			}
		}

		let displayed = showtimes.show;
		let loading = showtimes.loading;
		let showDummyElements = showtimes.loading === undefined ? true : showtimes.loading;

		// const showFooter = !state.filterSelection;
		const showtimesFooterBackgroundColorFallback = accessNested(props, 'store.appData.styling.variables.tertiaryColor');
		const hiddenFilters = !state.validCountry || this.noFiltersAvailable;

		const dualViewEnabled = isDualViewEnabled();

		let mobileOverlay;
		if (state.mobileOverlayActive) {
			mobileOverlay = <div class={s.mobileSearchOverlay} key="mobileOverlay">{this.renderLocationSearch(true)}</div>;
		}

		let mapThings = [
			(
				<div
					style={{ top: state.mapTop + 'px' }}
					class={joinClasses(s.mapModeContainer, (state.mode === 'map' && !state.mapTheaterExpanded) && s.mapModeVisible)}
					key="map-mode"
				>
					{this.renderModeSelector()}
				</div>
			),
			this.renderMap()
		];

		return (
			<div class={s.wrapper} style={style} key="wrapper">
				{mobileOverlay ? mobileOverlay : (
					<MovieDetails
						type="showtimes"
						tt={showtimesAppData.tt}
						bg={showtimesAppData.bg}
						buttons={showtimesAppData.buttons}
						visible={props.store.showtimes.show && state.ttVisible}
						hide={!state.ttVisible}
						copy={copy}
						appData={appData}
						cookieHeight={cookieHeight}
						cookieBannerOpen={evidonMode || cookieMode}
						key="movieDetails"
						dualViewEnabled={dualViewEnabled}
					/>
				)}
				<div
					class={joinClasses({
						s,
						container: true,
						animate: showtimes.animate,
						sonyHeader: sonyHeaderEnabled,
						showtimesOnlyIframed: isShowtimesOnlyIframed,
						displayed,
						loading,
						filterSelectionActive: state.filterSelection,
						mapTheaterExpanded: state.mapTheaterExpanded,
						animateTheaterExpansion: state.animateTheaterExpansion
					}, s['mode-' + state.mode])}
					style={containerStyle}
					ref={e => this.$container = e}
					key="container"
				>
					<div
						class={joinClasses(s.filterSelectionContainer, !this.state.sameCountry && s.disableFilters)}
						onClick={this.handleFilterOverlayClick}
						key="filters"
					>
						<FilterSelection
							appData={appData}
							client={client}
							filters={showtimes.filters}
							onSubmit={this.filtersChange}
							allShowtimesMovies={allShowtimesMovies}
							ref={e => this.$filterSelection = e}
						/>
					</div>
					{this.renderProviderInterstitial()}
					<div class={s.scroller} ref={e => this.$scroller = e} data-section="scroller" onClick={this.scrollerClick} onScroll={this.handleScroll} key="scroller">
						{(state.mode !== 'map' && !isShowtimesOnlyIframed) && <MovieDetailsClickableArea fallbackClickHandler={this.hideShowtimes} />}
						<div class={s.content} ref={e => this.$content = e} key="content">
							{dualViewEnabled && <DualMenu />}
							<div
								class={joinClasses(s.collapseHeader, dualViewEnabled && s.dualMenu)}
								ref={e => this.$collapseHeader = e}
								onTouchStart={this.onCHTouchStart}
								onTouchMove={this.onCHTouchMove}
								key="header"
							>
								{!state.mapTheaterExpanded && this.renderLocationSearch()}
							</div>
							<div class={joinClasses(s.sticky, hiddenFilters && s.hiddenFilters)} ref={e => this.$sticky = e} key="sticky">
								{this.renderCurrentFilters()}
							</div>
							<div class={s.listView} ref={e => this.$listView = e} key="list">
								<div class={s.listModeContainer} key="selector">{this.renderModeSelector()}</div>
								{this.renderVisualState()}
								{showDummyElements ? this.renderDummyElements(8) : this.renderElements()}
								<div class={s.suggestions} key="suggestions">
									{this.renderNearbyScreeningsLocations()}
								</div>
								{this.renderAttribution()}
								<div class={s.filler} key="filler" />
								<Footer
									data={props.store.appData.footer}
									parent="showtimes"
									scrollElem={scrollElem}
									fallBackColor={showtimesFooterBackgroundColorFallback}
									key="footer"
									store={props.store}
								/>
							</div>
						</div>
						{client.isDesktopSize && mapThings}
					</div>
					{!client.isDesktopSize && mapThings}
				</div>
			</div>
		);
	}
}
